import { AGraphRestrictionType, ProposablePortfolio_TEMP } from '@blk/aw-api-clients';
import { LockType, Portfolio, PositionLockOption } from '@blk/aw-bff-typescript-models';
import { safeNumber } from '@blk/aw-utils-core';
import { Position, PositionConstraint } from '@blk/awt-aladdinportfoliobuilderserver';
import { IRowNode } from 'ag-grid-community';
import { isNil, isNumber, isUndefined, split } from 'lodash';

export type RestrictionEditableColumnField = 'targetValue' | 'minTarget' | 'maxTarget';

export enum POS_INFO_LOCK_FIELD {
  LOCK = 'internal-awpg-lock',
  MAX_TARGET = 'internal-awpg-max-weight',
  MIN_TARGET = 'internal-awpg-min-weight',
  SELL_ALL = 'internal-awpg-sellAll',
  TARGET_VALUE = 'internal-awpg-weight',
}

export const getPosInfoField = (column: RestrictionEditableColumnField) => {
  switch (column) {
    case 'targetValue': {
      return POS_INFO_LOCK_FIELD.TARGET_VALUE;
    }
    case 'minTarget': {
      return POS_INFO_LOCK_FIELD.MIN_TARGET;
    }
    case 'maxTarget': {
      return POS_INFO_LOCK_FIELD.MAX_TARGET;
    }
  }
};

export enum POS_INFO_LOCK_VALUE {
  BUY = 'buy',
  FULL = 'full',
  NONE = 'none',
  REMOVED = 'removed',
  SELL = 'sell',
}

// Shape representing possible lock data that can exist within posInfo
export type PosInfoLockValues = {
  [POS_INFO_LOCK_FIELD.LOCK]?: 'true' | true | POS_INFO_LOCK_VALUE | LockType | 'false';
  [POS_INFO_LOCK_FIELD.TARGET_VALUE]?: number;
  [POS_INFO_LOCK_FIELD.MIN_TARGET]?: number;
  [POS_INFO_LOCK_FIELD.MAX_TARGET]?: number;
  [POS_INFO_LOCK_FIELD.SELL_ALL]?: 'true' | true;
};

export enum POSITION_LOCK_INSTRUCTION {
  CUSTOM_RANGE = 'none',
  DO_NOT_BUY = 'buy',
  DO_NOT_SELL = 'sell',
  LOCK_POSITION = 'full',
  REMOVE_LOCK = 'removeLock',
  SELECT = 'select',
  SELL_ALL = 'forceSell',
}

export const POSITION_LOCK_OPTIONS = {
  SELECT: POSITION_LOCK_INSTRUCTION.SELECT,
  LOCK_POSITION: POSITION_LOCK_INSTRUCTION.LOCK_POSITION,
  DO_NOT_BUY: POSITION_LOCK_INSTRUCTION.DO_NOT_BUY,
  DO_NOT_SELL: POSITION_LOCK_INSTRUCTION.DO_NOT_SELL,
  SELL_ALL: POSITION_LOCK_INSTRUCTION.SELL_ALL,
  CUSTOM_RANGE: POSITION_LOCK_INSTRUCTION.CUSTOM_RANGE,
  REMOVE_LOCK: POSITION_LOCK_INSTRUCTION.REMOVE_LOCK,
} satisfies Record<PositionLockOption, POSITION_LOCK_INSTRUCTION>;

export enum CAMPAIGN_RESTRICTION_TYPES {
  RESTRICTION_TYPE_SECURITY_BUY = 'RESTRICTION_TYPE_SECURITY_BUY',
  RESTRICTION_TYPE_SECURITY_BUY_AMOUNT = 'RESTRICTION_TYPE_SECURITY_BUY_AMOUNT',
  RESTRICTION_TYPE_SECURITY_EXPOSURE = 'RESTRICTION_TYPE_SECURITY_EXPOSURE',
  RESTRICTION_TYPE_SECURITY_SELL_ALL = 'RESTRICTION_TYPE_SECURITY_SELL_ALL',
  RESTRICTION_TYPE_SECURITY_SELL_ALL_AMOUNT = 'RESTRICTION_TYPE_SECURITY_SELL_ALL_AMOUNT',
}

export const POSITION_LOCK_INSTRUCTION_TO_CAMPAIGN_RESTRICTION_MAP: { [key: string]: string } = {
  [POSITION_LOCK_INSTRUCTION.SELL_ALL]: CAMPAIGN_RESTRICTION_TYPES.RESTRICTION_TYPE_SECURITY_SELL_ALL,
};

export const CAMPAIGN_RESTRICTION_TYPE_TO_POS_INFO_LOCK_FIELD_MAP: { [key: string]: string } = {
  [CAMPAIGN_RESTRICTION_TYPES.RESTRICTION_TYPE_SECURITY_SELL_ALL]: POS_INFO_LOCK_FIELD.SELL_ALL,
};

export const CAMPAIGN_RESTRICTION_TYPE_TO_POSITION_LOCK_INSTRUCTION_MAP: {
  [key: string]: POSITION_LOCK_INSTRUCTION;
} = {
  [CAMPAIGN_RESTRICTION_TYPES.RESTRICTION_TYPE_SECURITY_SELL_ALL]: POSITION_LOCK_INSTRUCTION.SELL_ALL,
};

export const getDecimalWeightFromMV = (
  marketValue: number | string | undefined,
  totalNav: number
): number | undefined => (typeof marketValue === 'number' ? safeNumber(marketValue / (totalNav || 1)) : undefined);

export const stripPosInfoPrefixWithFallback = (str: string): string => {
  const splitResult = split(str, new RegExp('^' + 'posInfo.'.replace('.', '\\.')));
  return splitResult.pop() ?? '';
};

// derive instruction from position.posInfo
export const getInstructionFromPosInfo = (
  isInstructionModified: boolean,
  posInfo?: PosInfoLockValues
): POSITION_LOCK_INSTRUCTION | undefined => {
  if (!posInfo) {
    return POSITION_LOCK_INSTRUCTION.SELECT;
  }

  const lock = posInfo[POS_INFO_LOCK_FIELD.LOCK];
  const sellAll = posInfo[POS_INFO_LOCK_FIELD.SELL_ALL];
  const minTarget: number | undefined = posInfo[POS_INFO_LOCK_FIELD.MIN_TARGET];
  const maxTarget: number | undefined = posInfo[POS_INFO_LOCK_FIELD.MAX_TARGET];
  const targetValue: number | undefined = posInfo[POS_INFO_LOCK_FIELD.TARGET_VALUE];

  if (
    sellAll === true ||
    sellAll === 'true' ||
    (lock === POSITION_LOCK_INSTRUCTION.DO_NOT_BUY && minTarget === 0 && maxTarget === 0)
  ) {
    return POSITION_LOCK_INSTRUCTION.SELL_ALL;
  } else if (lock === POS_INFO_LOCK_VALUE.REMOVED) {
    return POSITION_LOCK_INSTRUCTION.REMOVE_LOCK;
  } else if (lock === POS_INFO_LOCK_VALUE.FULL || lock === true || lock === 'true') {
    return POSITION_LOCK_INSTRUCTION.LOCK_POSITION;
  } else if (lock === POS_INFO_LOCK_VALUE.BUY) {
    return POSITION_LOCK_INSTRUCTION.DO_NOT_BUY;
  } else if (lock === POS_INFO_LOCK_VALUE.SELL) {
    return POSITION_LOCK_INSTRUCTION.DO_NOT_SELL;
  }
  const isCustomRange =
    (isNumber(minTarget) && minTarget >= 0 && ((isNumber(maxTarget) && maxTarget >= 0) || isUndefined(maxTarget))) ||
    (isNumber(maxTarget) && maxTarget >= 0 && ((isNumber(minTarget) && minTarget >= 0) || isUndefined(minTarget))) ||
    (isNumber(targetValue) && targetValue >= 0);

  if (isCustomRange) {
    return POSITION_LOCK_INSTRUCTION.CUSTOM_RANGE;
  }

  return isInstructionModified ? POSITION_LOCK_INSTRUCTION.SELECT : undefined;
};

// derive default instruction from proposal's optimizationContext.portfolioConstraints[0].positionConstraints
export const getDefaultInstructionFromOC = (
  positionConstraint: PositionConstraint
): POSITION_LOCK_INSTRUCTION | undefined => {
  const lockType = positionConstraint.lockType;
  const min = positionConstraint.weight?.min;
  const max = positionConstraint.weight?.max;
  if (lockType === POS_INFO_LOCK_VALUE.BUY && min === 0 && max === 0) {
    return POSITION_LOCK_INSTRUCTION.SELL_ALL;
  } else if (lockType === POS_INFO_LOCK_VALUE.FULL) {
    return POSITION_LOCK_INSTRUCTION.LOCK_POSITION;
  } else if (lockType === POS_INFO_LOCK_VALUE.BUY) {
    return POSITION_LOCK_INSTRUCTION.DO_NOT_BUY;
  } else if (lockType === POS_INFO_LOCK_VALUE.SELL) {
    return POSITION_LOCK_INSTRUCTION.DO_NOT_SELL;
  } else if (lockType === POS_INFO_LOCK_VALUE.NONE && isNumber(min) && isNumber(max)) {
    return POSITION_LOCK_INSTRUCTION.CUSTOM_RANGE;
  }
  return POSITION_LOCK_INSTRUCTION.SELECT;
};

// derive min/max weight (if presented) from position.posInfo
export const getTargetValuesFromPosInfo = (totalMV: number, posInfo?: PosInfoLockValues) => {
  let minTarget, maxTarget;
  if (posInfo) {
    minTarget = isNumber(posInfo[POS_INFO_LOCK_FIELD.MIN_TARGET])
      ? posInfo[POS_INFO_LOCK_FIELD.MIN_TARGET] * totalMV
      : undefined;
    maxTarget = isNumber(posInfo[POS_INFO_LOCK_FIELD.MAX_TARGET])
      ? posInfo[POS_INFO_LOCK_FIELD.MAX_TARGET] * totalMV
      : undefined;
  }
  return {
    minTarget,
    maxTarget,
  };
};

const isNonNegative = (num: string | number | undefined): num is number => isNumber(num) && num >= 0;
// get default min/max weight (if presented) from proposal's optimizationContext.portfolioConstraints[0].positionConstraints
export const getDefaultTargetValuesFromOC = (
  instruction: POSITION_LOCK_INSTRUCTION | undefined,
  positionMV: number,
  totalMV: number,
  positionConstraint?: PositionConstraint
) => {
  let minTarget, maxTarget;
  if (instruction) {
    switch (instruction) {
      case POSITION_LOCK_INSTRUCTION.DO_NOT_BUY:
        maxTarget = isNonNegative(positionConstraint?.weight?.max)
          ? positionConstraint.weight.max * totalMV
          : undefined;
        break;
      case POSITION_LOCK_INSTRUCTION.DO_NOT_SELL:
        minTarget = isNonNegative(positionConstraint?.weight?.min)
          ? positionConstraint.weight.min * totalMV
          : undefined;
        break;
      case POSITION_LOCK_INSTRUCTION.SELL_ALL:
        minTarget = 0;
        maxTarget = 0;
        break;
      case POSITION_LOCK_INSTRUCTION.LOCK_POSITION:
      case POSITION_LOCK_INSTRUCTION.CUSTOM_RANGE:
        minTarget = isNonNegative(positionConstraint?.weight?.min)
          ? positionConstraint.weight.min * totalMV
          : positionMV;
        maxTarget = isNonNegative(positionConstraint?.weight?.max)
          ? positionConstraint.weight.max * totalMV
          : positionMV;
        break;
      case POSITION_LOCK_INSTRUCTION.SELECT:
      default:
        break;
    }
  }
  return { minTarget, maxTarget };
};

export const isPositionLocked = (
  position: { posInfo: PosInfoLockValues | undefined } | undefined,
  direction: 'BUY' | 'SELL' | undefined
): boolean => {
  if (!position?.posInfo) {
    return false;
  }
  const { posInfo } = position;
  const lockValue = posInfo[POS_INFO_LOCK_FIELD.LOCK];
  const isSellAll =
    posInfo[POS_INFO_LOCK_FIELD.SELL_ALL] ||
    (lockValue === POSITION_LOCK_INSTRUCTION.DO_NOT_BUY &&
      posInfo[POS_INFO_LOCK_FIELD.MAX_TARGET] === 0 &&
      posInfo[POS_INFO_LOCK_FIELD.MIN_TARGET] === 0);
  if (lockValue === true || lockValue === 'true' || lockValue === POS_INFO_LOCK_VALUE.FULL || isSellAll) {
    return true;
  }
  if (direction === 'SELL') {
    return lockValue === POS_INFO_LOCK_VALUE.SELL;
  }
  if (direction === 'BUY') {
    return lockValue === POS_INFO_LOCK_VALUE.BUY;
  }
  if (lockValue === 'false') {
    return false;
  }
  return !isNil(lockValue);
};

export const getToggledLockingStatusAsString = (position: { posInfo: Position['posInfo'] }): 'true' | undefined => {
  const isLocked = 'true';
  if (isNil(position.posInfo['internal-awpg-lock'])) {
    return isLocked;
  }
  if (position.posInfo['internal-awpg-lock'] === isLocked) {
    return undefined;
  }
  return isLocked;
};

/**
 * Toggle lock of a position within a group with a given the current locking status of the group.
 * If the entire group is currently locked, toggle it to unlock. Otherwise, toggle it to lock. Returns boolean values
 * as string because AWPG only accepts strings and numbers.
 */
export const getGroupPositionLockToggleAsString = (isGroupLocked: boolean) => (isGroupLocked ? 'false' : 'true');

export const derivePosInfoLockDataFromPositionConstraint = (
  constraint: PositionConstraint,
  existingPosInfo: PosInfoLockValues | undefined
) => {
  const posInfo: PosInfoLockValues = { ...existingPosInfo };
  /*
   * Always set the lock field to 'none' instead of undefined to ensure downstream consumers receive an explicit value.
   * See [3413644] for business context.
   */
  posInfo[POS_INFO_LOCK_FIELD.LOCK] = constraint.lockType === 'none' ? POS_INFO_LOCK_VALUE.NONE : constraint.lockType;
  if (!isNil(constraint.weight?.min) || !isNil(existingPosInfo?.[POS_INFO_LOCK_FIELD.MIN_TARGET])) {
    posInfo[POS_INFO_LOCK_FIELD.MIN_TARGET] = constraint.weight?.min;
  }
  if (!isNil(constraint.weight?.max) || !isNil(existingPosInfo?.[POS_INFO_LOCK_FIELD.MAX_TARGET])) {
    posInfo[POS_INFO_LOCK_FIELD.MAX_TARGET] = constraint.weight?.max;
  }
  return posInfo;
};

export const derivePosInfoLockDataFromInstruction = ({
  instruction,
  position,
  totalMV,
}: {
  instruction: POSITION_LOCK_INSTRUCTION;
  position:
    | {
        posInfo: PosInfoLockValues;
        marketValue?: number;
      }
    | undefined;
  totalMV: number;
}): Omit<PosInfoLockValues, POS_INFO_LOCK_FIELD.TARGET_VALUE> => {
  const computeWeight = (marketValue: number | undefined) =>
    isNumber(marketValue) && totalMV ? getDecimalWeightFromMV(marketValue, totalMV) : undefined;
  let expected: 'true' | POS_INFO_LOCK_VALUE | undefined = undefined;
  let minTarget: number | undefined;
  let maxTarget: number | undefined;
  switch (instruction) {
    case POSITION_LOCK_INSTRUCTION.LOCK_POSITION:
      expected = POS_INFO_LOCK_VALUE.FULL;
      minTarget = computeWeight(position?.marketValue);
      maxTarget = computeWeight(position?.marketValue);
      break;
    case POSITION_LOCK_INSTRUCTION.DO_NOT_BUY:
      expected = POS_INFO_LOCK_VALUE.BUY;
      minTarget = undefined;
      maxTarget = computeWeight(position?.marketValue);
      break;
    case POSITION_LOCK_INSTRUCTION.DO_NOT_SELL:
      expected = POS_INFO_LOCK_VALUE.SELL;
      minTarget = computeWeight(position?.marketValue);
      maxTarget = undefined;
      break;
    case POSITION_LOCK_INSTRUCTION.SELL_ALL:
      expected = undefined;
      minTarget = 0;
      maxTarget = 0;
      break;
    case POSITION_LOCK_INSTRUCTION.CUSTOM_RANGE:
      expected = POS_INFO_LOCK_VALUE.NONE;
      minTarget = position?.posInfo[POS_INFO_LOCK_FIELD.MIN_TARGET] ?? 0;
      maxTarget = position?.posInfo[POS_INFO_LOCK_FIELD.MAX_TARGET];
      break;
    case POSITION_LOCK_INSTRUCTION.REMOVE_LOCK:
      expected = POS_INFO_LOCK_VALUE.REMOVED;
      minTarget = undefined;
      maxTarget = undefined;
      break;
    case POSITION_LOCK_INSTRUCTION.SELECT:
      expected = undefined;
      minTarget = undefined;
      maxTarget = undefined;
      break;
    default:
      expected = undefined;
      minTarget = undefined;
      maxTarget = undefined;
      break;
  }
  const nextPosInfo: PosInfoLockValues & { [POS_INFO_LOCK_FIELD.TARGET_VALUE]?: never } = {}; // do not allow setting target value as part of instruction change
  // update corresponding lock field
  nextPosInfo[POS_INFO_LOCK_FIELD.LOCK] = expected;
  if (instruction === POSITION_LOCK_INSTRUCTION.SELL_ALL) {
    nextPosInfo[POS_INFO_LOCK_FIELD.SELL_ALL] = true;
  } else {
    nextPosInfo[POS_INFO_LOCK_FIELD.SELL_ALL] = undefined;
  }
  // update numeric fields
  nextPosInfo[POS_INFO_LOCK_FIELD.MIN_TARGET] = minTarget;
  nextPosInfo[POS_INFO_LOCK_FIELD.MAX_TARGET] = maxTarget;
  if (position?.posInfo[POS_INFO_LOCK_FIELD.TARGET_VALUE]) {
    nextPosInfo[POS_INFO_LOCK_FIELD.TARGET_VALUE] = undefined;
  }
  return nextPosInfo;
};

export const isGroupLocked = (node: IRowNode<{ posInfo: PosInfoLockValues }>, direction: 'BUY' | 'SELL' | undefined) =>
  !!node.allLeafChildren?.every((row) => row.data && isPositionLocked(row.data, direction));

const LOCK_TYPE_TO_RESTRICTION_TYPE: Record<string, AGraphRestrictionType> = {
  full: 'RESTRICTION_TYPE_POSITION_HOLD',
  sell: 'RESTRICTION_TYPE_POSITION_DO_NOT_SELL',
  buy: 'RESTRICTION_TYPE_POSITION_DO_NOT_BUY',
  forceSell: 'RESTRICTION_TYPE_POSITION_SELL_ALL',
  none: 'RESTRICTION_TYPE_POSITION_EXPOSURE',
  removeLock: 'RESTRICTION_TYPE_POSITION_EXPOSURE',
};

export const mapLockTypeToRestrictionType = (lockType?: string): AGraphRestrictionType =>
  LOCK_TYPE_TO_RESTRICTION_TYPE[lockType ?? ''];

export const getConstraintForPosition = (
  constraints: PositionConstraint[] | undefined,
  positionId: string
): PositionConstraint | undefined => constraints?.find((constraint) => constraint.positionId === positionId);

/**
 * 2924218 - force reset posInfo to originalAggregate values only if no position constraints exist.
 * This allows the "instructions" column to accurately reflect the user selection by not storing outdated posInfo values.
 * @param positionConstraint
 * @param portfolioGroupPortfolios
 * @param originalAggregatePortfolios
 * @param portfolioId
 * @param viablePositionId
 */
export const resetPosInfoToOriginalWhenNoPositionConstraints = (
  positionConstraint: PositionConstraint | undefined,
  portfolioGroupPortfolios: ProposablePortfolio_TEMP[],
  originalAggregatePortfolios: Portfolio[] | undefined,
  portfolioId: string,
  viablePositionId: string
): void => {
  if (positionConstraint) {
    return;
  }
  const draftPosition = portfolioGroupPortfolios
    .find((portfolio) => portfolio.id === portfolioId)
    ?.positions.find((position) => position.id === viablePositionId);
  const originalPosition = originalAggregatePortfolios
    ?.find((portfolio) => portfolio.id === portfolioId)
    ?.positions.find((position) => position.id === viablePositionId);
  if (draftPosition && originalPosition) {
    draftPosition.posInfo = originalPosition.posInfo;
  }
};
